import { ArrowRight, ChevronRight, MessageSquare } from 'lucide-react';
import { cn } from '../../utils';
import { usePrimaryFill } from '../../hooks/usePrimaryFill';
import { useT } from '../../i18n';
import type { MessageTemplate } from '../../types';
import styles from './FAQInitialScreen.module.css';

export interface FAQInitialScreenProps {
  greetingText?: string;
  howCanWeHelpText?: string;
  trendingTitle?: string;
  messageTemplates?: MessageTemplate[];
  width?: number;
  showGreeting?: boolean;
  logoUrl?: string;
  askWhatYouWantText?: string;
  primaryColor?: string;
  /** Fill applied on hover of the CTA button */
  primaryHoverColor?: string;
  useGradient?: boolean;
  onTemplateClick?: (template: MessageTemplate) => void;
  onStartChat?: () => void;
  className?: string;
}

const DEFAULT_LOGO_URL =
  'https://iwjbgyrjrayialdsfobn.supabase.co/storage/v1/object/public/assets/pleiful-logo.png';

const DEFAULT_PRIMARY_COLOR = '#1e293b';

export function FAQInitialScreen({
  greetingText,
  howCanWeHelpText,
  trendingTitle,
  messageTemplates = [],
  width = 384,
  showGreeting = true,
  logoUrl,
  askWhatYouWantText,
  primaryColor = DEFAULT_PRIMARY_COLOR,
  primaryHoverColor,
  useGradient = false,
  onTemplateClick,
  onStartChat,
  className,
}: FAQInitialScreenProps) {
  const t = useT();
  const logoSource = logoUrl || DEFAULT_LOGO_URL;
  const askText = askWhatYouWantText ?? t('faq.askWhatYouWant');
  // Absent is the widget's own line; empty is a channel that has none.
  const helpText = howCanWeHelpText ?? t('faq.howCanWeHelp');

  const {
    style: startFillStyle,
    onMouseEnter: onStartButtonEnter,
    onMouseLeave: onStartButtonLeave,
  } = usePrimaryFill(primaryColor, primaryHoverColor, useGradient);

  const handleTemplateClick = (template: MessageTemplate) => {
    onTemplateClick?.(template);
  };

  return (
    <div className={cn(styles.container, className)}>
      <div className={styles.scrollArea}>
        <div className={styles.content}>
          {showGreeting && (
            <div className={styles.logoSection}>
              <img
                src={logoSource}
                alt={t('common.logoAlt')}
                className={styles.logo}
                style={{
                  maxWidth: width ? `${Math.min(width * 0.2, 128)}px` : undefined,
                }}
                draggable={false}
              />
            </div>
          )}

          {showGreeting && (
            <section className={styles.greetingSection}>
              <div className={styles.greetingContent}>
                <h2 className={styles.greetingTitle}>{greetingText ?? t('faq.greeting')}</h2>
                {helpText && <p className={styles.greetingSubtitle}>{helpText}</p>}
              </div>
            </section>
          )}

          <div className={styles.buttonSection}>
            <button
              onClick={onStartChat}
              className={styles.primaryButton}
              aria-label={askText}
              type="button"
              style={startFillStyle}
              onMouseEnter={onStartButtonEnter}
              onMouseLeave={onStartButtonLeave}
            >
              <div className={styles.buttonContent}>
                <div className={styles.buttonIcon}>
                  <MessageSquare size={18} />
                </div>
                <span className={styles.buttonText}>{askText}</span>
              </div>
              <ArrowRight size={20} strokeWidth={2.5} />
            </button>
          </div>

          {messageTemplates && messageTemplates.length > 0 && (
            <div className={styles.templatesSection}>
              <nav className={styles.templatesNav}>
                <div className={styles.templatesTitle}>{trendingTitle ?? t('faq.trending')}</div>
                <div className={styles.templatesList}>
                  {messageTemplates.map((template, index) => (
                    <button
                      key={`${template.search_query || template.form_id}-${index}`}
                      onClick={() => handleTemplateClick(template)}
                      className={styles.templateButton}
                      aria-label={t('faq.selectTemplate', { label: template.label })}
                      type="button"
                    >
                      <div className={styles.templateContent}>
                        {template.emoji && (
                          template.emoji.startsWith('http') ? (
                            <img
                              src={template.emoji}
                              alt=""
                              className={styles.templateEmoji}
                              draggable={false}
                            />
                          ) : (
                            <span className={styles.templateEmoji}>{template.emoji}</span>
                          )
                        )}
                        <span className={styles.templateLabel}>{template.label}</span>
                      </div>
                      <ChevronRight size={18} />
                    </button>
                  ))}
                </div>
              </nav>
            </div>
          )}

          <p className={styles.legalText}>
            {t('faq.legalPrefix')}{' '}
            <a
              href="https://kapia.co/legal/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.legalLink}
            >
              {t('faq.legalPrivacy')}
            </a>{' '}
            {t('faq.legalAnd')}{' '}
            <a
              href="https://kapia.co/legal/terms-conditions"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.legalLink}
            >
              {t('faq.legalTerms')}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
